// React error boundary — catches render exceptions below it, shows recovery UI
// with a retry button, and reports the crash through the one renderer error
// reporter (core/rendererErrorReporter.ts), which also carries the errors no
// boundary can see.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@onestone/ui/button';
import { report, resetDedupe } from '@/core/rendererErrorReporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** When this changes, a caught error is cleared — pass the route pathname so
   *  navigating away from a crashed page auto-recovers. */
  resetKey?: string | number;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Stale-chunk detection: after a rebuild, old cached chunks reference
    // exports that no longer exist. Auto-reload recovers without manual
    // intervention. One-shot flag prevents infinite reload loops.
    if (isStaleChunkError(error) && !sessionStorage.getItem('chunk-reload')) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Preserve DevTools console signal for the operator-running-the-app case.
    console.error('ErrorBoundary caught:', error, info.componentStack);
    // The reporter dedupes repeats (a render storm re-throwing on every
    // Try-Again) and never throws, so it cannot mask the original error.
    report({ source: 'boundary', error, componentStack: info.componentStack ?? undefined });
  }

  override componentDidUpdate(prevProps: Props): void {
    if (prevProps.resetKey !== this.props.resetKey) {
      // Route changed — clear both the visible error state and the reported-
      // signature cache so a different page can report its own crashes.
      resetDedupe();
      if (this.state.error !== null) this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div data-testid="error-boundary" className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-6 text-center">
            <h2 className="mb-2 text-sm font-semibold text-destructive">Something went wrong</h2>
            <p data-testid="error-boundary-message" className="mb-4 max-w-md font-mono text-xs text-muted-foreground">
              {this.state.error.message}
            </p>
            <Button
              data-testid="error-boundary-retry"
              size="sm"
              variant="outline"
              onClick={() => this.setState({ error: null })}
            >
              Try Again
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Detect stale-chunk / mixed-asset-version errors that require a hard reload. */
function isStaleChunkError(error: Error): boolean {
  const msg = error.message;
  return (
    msg.includes("doesn't provide an export named") ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  );
}
